import Dexie, { type Table } from 'dexie';
import type { Product } from '@/types/product.types';
import type { ZoneStockage } from '@/types/inventory.types';

/**
 * Statut de synchronisation pour les entités locales
 */
export type LocalSyncStatus = 'synced' | 'pending' | 'conflict';

/**
 * Produit stocké localement avec métadonnées de sync
 */
export interface LocalProduct extends Product {
  _syncStatus: LocalSyncStatus;
  _lastModified: number;
  _serverUpdatedAt?: string;
}

/**
 * Stock stocké localement avec métadonnées de sync
 */
export interface LocalStock {
  id: string;
  restaurant_id: string;
  produit_id: string;
  zone: ZoneStockage;
  quantite: number;
  derniere_mise_a_jour: string;
  updated_by?: string | null;
  _syncStatus: LocalSyncStatus;
  _lastModified: number;
  _serverUpdatedAt?: string;
}

/**
 * Type d'opération dans la queue de synchronisation
 */
export type SyncOperation = 'INSERT' | 'UPDATE' | 'DELETE';

/**
 * Table concernée par l'opération
 */
export type SyncTable = 'produits' | 'stocks';

/**
 * Item dans la queue de synchronisation
 */
export interface SyncQueueItem {
  id?: number;
  operation: SyncOperation;
  table: SyncTable;
  recordId: string;
  data: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
  lastError?: string;
  restaurantId: string;
}

/**
 * Configuration de la base de données Dexie (IndexedDB)
 */
class AppDatabase extends Dexie {
  products!: Table<LocalProduct, string>;
  stocks!: Table<LocalStock, string>;
  syncQueue!: Table<SyncQueueItem, number>;

  constructor() {
    super('stockchef-db');

    // Définition du schéma
    // Clé primaire + index pour les requêtes fréquentes
    this.version(1).stores({
      products: 'id, restaurant_id, nom, categorie, _syncStatus',
      stocks: 'id, restaurant_id, produit_id, zone, [restaurant_id+zone], _syncStatus',
      syncQueue: '++id, table, timestamp, restaurantId',
    });
  }

  /**
   * Vider toutes les données locales (pour déconnexion)
   */
  async clearAll(): Promise<void> {
    await this.products.clear();
    await this.stocks.clear();
    await this.syncQueue.clear();
  }

  /**
   * Vider les données d'un restaurant spécifique
   */
  async clearRestaurantData(restaurantId: string): Promise<void> {
    await this.products.where('restaurant_id').equals(restaurantId).delete();
    await this.stocks.where('restaurant_id').equals(restaurantId).delete();
    await this.syncQueue.where('restaurantId').equals(restaurantId).delete();
  }

  /**
   * Obtenir le nombre d'opérations en attente
   */
  async getPendingCount(): Promise<number> {
    return await this.syncQueue.count();
  }

  /**
   * Obtenir le nombre de produits/stocks en attente de sync
   */
  async getPendingSyncCount(): Promise<{ products: number; stocks: number }> {
    const [products, stocks] = await Promise.all([
      this.products.where('_syncStatus').equals('pending').count(),
      this.stocks.where('_syncStatus').equals('pending').count(),
    ]);
    return { products, stocks };
  }
}

// Instance unique de la base de données
export const db = new AppDatabase();

// Helper pour créer un LocalProduct à partir d'un Product
export function toLocalProduct(
  product: Product,
  syncStatus: LocalSyncStatus = 'synced'
): LocalProduct {
  return {
    ...product,
    _syncStatus: syncStatus,
    _lastModified: Date.now(),
    _serverUpdatedAt: product.updated_at,
  };
}

// Helper pour créer un LocalStock
export function toLocalStock(
  stock: {
    id: string;
    restaurant_id: string;
    produit_id: string;
    zone: ZoneStockage;
    quantite: number;
    derniere_mise_a_jour?: string;
    updated_by?: string | null;
  },
  syncStatus: LocalSyncStatus = 'synced'
): LocalStock {
  return {
    id: stock.id,
    restaurant_id: stock.restaurant_id,
    produit_id: stock.produit_id,
    zone: stock.zone,
    quantite: stock.quantite,
    derniere_mise_a_jour: stock.derniere_mise_a_jour || new Date().toISOString(),
    updated_by: stock.updated_by,
    _syncStatus: syncStatus,
    _lastModified: Date.now(),
  };
}
